import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Search, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ReguaTimeline } from '@/components/reguas/ReguaTimeline';
import type { CollectionStep } from '@/components/reguas/ReguaTimeline';
import { ReguaModal } from '@/components/reguas/ReguaModal';
import { CobrancaModal } from '@/components/reguas/CobrancaModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ESTADOS_BRASILEIROS } from '@/lib/estados';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CollectionRule {
  id: string;
  name: string;
  trigger_days: number;
  channel: 'whatsapp' | 'email' | 'sms';
  is_active: boolean | null;
  company_id: string;
  idade_min: number | null;
  idade_max: number | null;
  menos_que: boolean | null;
  dias_atraso: number | null;
  estado: string | null;
  cidade: string | null;
  bairro: string | null;
  prioridade: number | null;
}

interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
}

function getCriterioText(rule: CollectionRule): string {
  const parts: string[] = [];
  
  if (rule.dias_atraso !== null && rule.dias_atraso !== undefined) {
    if (rule.menos_que) {
      parts.push(`ATRASOU MENOS QUE ${rule.dias_atraso} DIAS`);
    } else {
      parts.push(`ATRASOU ${rule.dias_atraso} DIAS OU MAIS`);
    }
  }
  
  if (rule.idade_min && rule.idade_max) {
    parts.push(`ENTRE ${rule.idade_min} E ${rule.idade_max} ANOS`);
  } else if (rule.idade_min) {
    parts.push(`A PARTIR DE ${rule.idade_min} ANOS`);
  } else if (rule.idade_max) {
    parts.push(`ATÉ ${rule.idade_max} ANOS`);
  }
  
  if (rule.estado) {
    const estadoNome = ESTADOS_BRASILEIROS.find(e => e.sigla === rule.estado)?.nome || rule.estado;
    if (rule.cidade) {
      parts.push(rule.bairro ? `${rule.bairro}, ${rule.cidade}` : rule.cidade);
    } else {
      parts.push(estadoNome);
    }
  }
  
  return parts.join(' • ') || 'TODOS OS CLIENTES';
}

function formatCPFCNPJ(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 11) {
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (numbers.length === 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

const ReguaDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('vinculados');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch rule data
  const { data: regua, isLoading } = useQuery({
    queryKey: ['collection-rule', id],
    queryFn: async () => {
      if (!id) throw new Error('ID not provided');
      const { data, error } = await supabase
        .from('collection_rules')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as CollectionRule;
    },
    enabled: !!id
  });

  // Fetch customers - for now returning all customers, in production this would filter by rule criteria
  const { data: customers = [] } = useQuery({
    queryKey: ['regua-customers', id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('customers')
        .select('id, name, tax_id, email, phone')
        .eq('company_id', profile.company_id)
        .limit(50);

      if (error) throw error;
      return data as Customer[];
    },
    enabled: !!id
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      const { error } = await supabase
        .from('collection_rules')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
      navigate('/regua');
    }
  });

  const handleEditStep = (step: CollectionStep) => {
    setSelectedStep(step);
    setStepModalOpen(true);
  };

  const handleAddStep = () => {
    setSelectedStep(null);
    setStepModalOpen(true);
  };

  const filteredCustomers = customers.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || 
           c.tax_id.includes(q);
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!regua) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Régua não encontrada.</p>
      </div>
    );
  }

  const criterioText = getCriterioText(regua);

  return (
    <div className="space-y-6">
      {/* Rule Card with Header */}
      <div className="bg-card rounded-lg border overflow-hidden">
        {/* Header row */}
        <div className="flex items-center gap-4 p-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/regua')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          {/* Rule name */}
          <h1 className="font-bold text-lg text-foreground">{regua.name}</h1>

          {/* Customer count */}
          <span className="text-muted-foreground whitespace-nowrap">
            <b className="text-foreground">{customers.length}</b> clientes
          </span>

          {/* Criteria badge */}
          <div className="flex-1 flex justify-center">
            <span className="inline-flex items-center px-3 py-1.5 rounded border bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {criterioText}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9 hover:bg-primary/10 hover:text-primary"
                    onClick={handleAddStep}
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Adicionar comunicação</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 hover:bg-primary/10 hover:text-primary"
              onClick={() => setEditModalOpen(true)}
            >
              <Pencil className="h-5 w-5" />
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Timeline */}
        <div className="border-t py-6 px-4 flex justify-center bg-muted/30">
          <ReguaTimeline 
            ruleId={regua.id} 
            onAddStep={handleAddStep} 
            onEditStep={handleEditStep} 
          />
        </div>
      </div>

      {/* Customers Section with Tabs */}
      <div className="bg-card rounded-lg border overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between p-4 border-b">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="vinculados" className="text-xs uppercase">
                Clientes de {regua.name}
              </TabsTrigger>
              <TabsTrigger value="elegiveis" className="text-xs uppercase">
                Clientes Elegíveis
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="vinculados" className="m-0">
            {/* Search and actions */}
            <div className="flex items-center gap-3 p-4 border-b">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou CPF/CNPJ"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" className="gap-2">
                <Upload className="h-4 w-4" />
                Importar
              </Button>
              <Button className="gap-2 hover-pop">
                <Plus className="h-4 w-4" />
                Novo Cliente
              </Button>
            </div>

            {/* Customer List Header */}
            <div className="grid grid-cols-[2fr_1fr_100px_150px_100px] gap-4 p-4 bg-muted/30 border-b text-xs font-bold text-muted-foreground uppercase tracking-wider">
              <span>Nome</span>
              <span>Documento</span>
              <span>Dia Venc.</span>
              <span>Régua</span>
              <span className="text-right">Ações</span>
            </div>

            {/* Customer List */}
            {filteredCustomers.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {search ? 'Nenhum cliente encontrado.' : 'Nenhum cliente vinculado a esta régua.'}
              </div>
            ) : (
              <div className="divide-y">
                {filteredCustomers.map((customer) => (
                  <div 
                    key={customer.id}
                    className="grid grid-cols-[2fr_1fr_100px_150px_100px] gap-4 p-4 items-center hover:bg-muted/30 transition-colors"
                  >
                    <span className="font-medium text-foreground">{customer.name}</span>
                    <span className="text-muted-foreground font-mono text-sm">
                      {formatCPFCNPJ(customer.tax_id)}
                    </span>
                    <span className="text-muted-foreground">Dia 05</span>
                    <span className="text-primary font-medium">{regua.name}</span>
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 hover:bg-primary/10 hover:text-primary"
                        onClick={() => navigate(`/clientes/${customer.id}`)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="elegiveis" className="m-0">
            {/* Search and actions */}
            <div className="flex items-center gap-3 p-4 border-b">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou CPF/CNPJ"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Customer List Header */}
            <div className="grid grid-cols-[2fr_1fr_100px_150px_100px] gap-4 p-4 bg-muted/30 border-b text-xs font-bold text-muted-foreground uppercase tracking-wider">
              <span>Nome</span>
              <span>Documento</span>
              <span>Dia Venc.</span>
              <span>Régua Atual</span>
              <span className="text-right">Ações</span>
            </div>

            {/* Eligible Customers - showing customers not in this rule */}
            <div className="p-8 text-center text-muted-foreground">
              <p className="mb-2">Clientes que atendem aos critérios desta régua mas estão em outra.</p>
              <p className="text-sm">Esta funcionalidade requer implementação do motor de elegibilidade.</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Rule Modal */}
      <ReguaModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        rule={regua}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['collection-rule', id] });
          queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
        }}
      />

      {/* Add/Edit Step Modal */}
      {id && (
        <CobrancaModal
          open={stepModalOpen}
          onOpenChange={setStepModalOpen}
          ruleId={id}
          step={selectedStep}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['collection-steps', id] });
          }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Régua"
        description={`Tem certeza que deseja excluir a régua "${regua.name}"? Esta ação não pode ser desfeita e todas as comunicações configuradas serão perdidas.`}
        onConfirm={() => deleteMutation.mutate()}
        confirmText="Excluir"
        variant="destructive"
      />
    </div>
  );
};

export default ReguaDetalhe;
